import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HappyIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ReceiptIcon from '@mui/icons-material/Receipt';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import UndoIcon from '@mui/icons-material/Undo';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TimelapseIcon from '@mui/icons-material/Timelapse';
import PaymentsIcon from '@mui/icons-material/Payments';
import EventIcon from '@mui/icons-material/Event';
import WarningIcon from '@mui/icons-material/Warning';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import TodayIcon from '@mui/icons-material/Today';

/**
 * The dashboard renders every block of the API response that is a `[{ title, sub_title, value }]`
 * array, whatever its key. These only tune the display: a nicer heading and a preferred order.
 * Blocks not listed here still show, after these, with a heading made from their key.
 */
const SECTION_HEADINGS = {
  sales: 'Orders & revenue',
  order_summary: 'Orders summary',
  taskSummary: 'Tasks summary',
  leads_summary: 'Leads summary',
  lead_summary: 'Leads summary',
  dba_counts: 'DBA summary',
  meetings: 'Meetings',
};

export const SECTION_ORDER = Object.keys(SECTION_HEADINGS);

/** `lead_summary` -> "Lead Summary", `taskSummary` -> "Task Summary" */
export const sectionHeading = (key) =>
  SECTION_HEADINGS[key] ??
  String(key)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

/** Titles whose value is an amount of money rather than a count. */
const MONEY_TITLES = new Set([
  'Invoiced Amount',
  'Outstanding Amount',
  'Revenue',
  'Refunds',
  'Average Order Value',
]);

// Titles not listed above that still read as money ("Total Amount", "Refunds", "Average Order Value"…).
const MONEY_PATTERN = /(amount|revenue|^refunds?$|average.*value|price|balance)/i;

export const isMoneyTitle = (title) => MONEY_TITLES.has(title) || MONEY_PATTERN.test(title);

const lead = (metric, status) => ({ module: 'leads', metric, ...(status && { status }) });
const order = (metric) => ({ module: 'orders', metric });
const invoice = (metric) => ({ module: 'invoices', metric });
const task = (metric) => ({ module: 'tasks', metric });
const orderStatus = (status) => ({ module: 'orders', listStatus: status });
const meeting = (status) => ({ module: 'meetings', metric: status, label: `${status} meetings` });

/**
 * Presentation per card, keyed by the API `title` (the API sends no key).
 * A title that is not listed still renders, with the default icon/colour and no drill-down.
 */
const CARD_META = {
  // leads_summary
  'Total Leads': { Icon: HappyIcon, color: 'success', drilldown: lead('total') },
  Overdue: { Icon: ScheduleIcon, color: 'warning', drilldown: lead('overdue') },
  'Total Overdues': { Icon: ScheduleIcon, color: 'warning', drilldown: lead('overdue') },
  'Approved Leads': { Icon: CheckCircleIcon, color: 'success', drilldown: lead('status', 'Approved') },
  'On Hold Leads': { Icon: ScheduleIcon, color: 'info', drilldown: lead('status', 'On Hold') },
  'Sales Generated': { Icon: TrendingUpIcon, color: 'success', drilldown: lead('converted') },
  'Declined Leads': { Icon: AssessmentIcon, color: 'error', drilldown: lead('status', 'Declined') },
  'Not Interested Leads': { Icon: AssessmentIcon, color: 'warning', drilldown: lead('status', 'Not Interested') },
  'Follow-up Leads': { Icon: ScheduleIcon, color: 'info', drilldown: lead('status', 'Follow Up') },
  'In-Process Leads': { Icon: PeopleIcon, color: 'primary', drilldown: lead('status', 'In Process') },
  'Pending Leads': { Icon: ScheduleIcon, color: 'warning', drilldown: lead('status', 'Pending') },

  // sales
  'Total Orders': { Icon: ShoppingCartIcon, color: 'primary', drilldown: order('total') },
  'Partially Paid Orders': { Icon: TimelapseIcon, color: 'info', drilldown: order('partial') },
  'Cancelled Orders': { Icon: CancelOutlinedIcon, color: 'default', drilldown: orderStatus('cancelled') },
  'Refunded Orders': { Icon: UndoIcon, color: 'error', drilldown: orderStatus('refunded') },
  'Chargeback Orders': { Icon: WarningIcon, color: 'error', drilldown: orderStatus('chargeback') },
  'Voided Orders': { Icon: LockOutlinedIcon, color: 'default', drilldown: orderStatus('voided') },
  'Orders Placed': { Icon: ShoppingCartIcon, color: 'primary', drilldown: order('total') },
  'Paid Orders': { Icon: CheckCircleIcon, color: 'success', drilldown: order('paid') },
  'Partial Orders': { Icon: TimelapseIcon, color: 'info', drilldown: order('partial') },
  'Unpaid Orders': { Icon: ScheduleIcon, color: 'warning', drilldown: order('unpaid') },
  'Converted Leads': { Icon: HappyIcon, color: 'success', drilldown: lead('converted') },
  'Invoiced Amount': { Icon: ReceiptIcon, color: 'primary', drilldown: invoice('invoiced') },
  'Outstanding Amount': { Icon: HourglassEmptyIcon, color: 'warning', drilldown: invoice('outstanding') },
  Revenue: { Icon: AttachMoneyIcon, color: 'success', drilldown: invoice('revenue') },
  Refunds: { Icon: UndoIcon, color: 'error', drilldown: invoice('refunds') },
  'Payments Count': { Icon: PaymentsIcon, color: 'info' },
  'Average Order Value': { Icon: TrendingUpIcon, color: 'secondary', drilldown: order('total') },

  // users (built from `total_active_users`)
  'Total Users': { Icon: PeopleIcon, color: 'primary', drilldown: { module: 'users', metric: 'active' } },

  // meetings
  'Upcoming Meetings': { Icon: EventIcon, color: 'primary', drilldown: meeting('Upcoming') },
  'Missed Meetings': { Icon: WarningIcon, color: 'error', drilldown: meeting('Missed') },
  'Rescheduled Meetings': { Icon: EventIcon, color: 'info', drilldown: meeting('Rescheduled') },
  'Completed Meetings': { Icon: CheckCircleIcon, color: 'success', drilldown: meeting('Completed') },

  // taskSummary
  'Total Tasks': { Icon: AssignmentIcon, color: 'primary', drilldown: task('total') },
  'Pending Tasks': { Icon: PendingActionsIcon, color: 'warning', drilldown: task('pending') },
  'In Progress': { Icon: PlayCircleOutlineIcon, color: 'info', drilldown: task('in_progress') },
  'Completed Tasks': { Icon: CheckCircleIcon, color: 'success', drilldown: task('completed') },
  'Cancelled Tasks': { Icon: CancelOutlinedIcon, color: 'error', drilldown: task('cancelled') },
  'Closed Tasks': { Icon: LockOutlinedIcon, color: 'default', drilldown: task('closed') },
  "Today's Deadlines": { Icon: TodayIcon, color: 'secondary', drilldown: task('today_deadline') },
};

const DEFAULT_META = { Icon: AssessmentIcon, color: 'primary' };

/** Icon/colour for a title that has no entry above, guessed from the words in it. */
const GUESSES = [
  [/declin|chargeback|refund|missed|fail|reject|cancel|void/i, { Icon: WarningIcon, color: 'error' }],
  [/partial|process|progress/i, { Icon: TimelapseIcon, color: 'info' }],
  [/overdue|pending|unpaid|hold|follow|outstanding|upcoming|reschedul|not interested|deadline/i, { Icon: ScheduleIcon, color: 'warning' }],
  [/paid|approved|complete|revenue|sales|converted|success|won/i, { Icon: CheckCircleIcon, color: 'success' }],
  [/order|payment|invoice/i, { Icon: ShoppingCartIcon, color: 'primary' }],
  [/meeting|event/i, { Icon: EventIcon, color: 'primary' }],
  [/task/i, { Icon: AssignmentIcon, color: 'primary' }],
  [/user|team|merchant|customer/i, { Icon: PeopleIcon, color: 'primary' }],
];

/**
 * Presentation for a card. Titles in CARD_META also get their click-through (`drilldown`);
 * any other title is styled from its wording and is simply not clickable.
 */
const ORDER_PAYMENT_METRICS = ['paid', 'partial', 'unpaid'];

/** A card that counts leads in one status (what the "Leads status" chart plots). */
export const isLeadStatusItem = (item) =>
  getCardMeta(item.title, item.sectionKey).drilldown?.metric === 'status';

/** A card that counts orders by payment state (what the "Order payment status" chart plots). */
export const orderPaymentMetric = (item) => {
  const d = getCardMeta(item.title, item.sectionKey).drilldown;
  return d?.module === 'orders' && ORDER_PAYMENT_METRICS.includes(d.metric) ? d.metric : null;
};

const LEAD_STATUS_BLOCK = /lead.*status|status.*lead/i;

/**
 * `sectionKey` is the API key the card came from: in a block like `leads_by_status` every
 * card is a lead status, so it opens the lead list filtered to the status named by its title.
 */
export const getCardMeta = (title, sectionKey = '') => {
  const base = CARD_META[title] ?? GUESSES.find(([re]) => re.test(title))?.[1] ?? DEFAULT_META;
  return LEAD_STATUS_BLOCK.test(sectionKey)
    ? { ...base, drilldown: { module: 'leads', metric: 'status', status: title } }
    : base;
};
